import React, { useState } from 'react';
import { renderMarkdown } from '../../markdown';

/* Feedback + flow-control components (registry §5–6). */

const VERDICT = {
  correct: { label: 'Nice work — you nailed it!', icon: '✓' },
  partial: { label: 'Getting there', icon: '◐' },
  incorrect: { label: 'Not quite', icon: '✗' },
};

const Stars = ({ earned, max }) => {
  const filled = max > 0 ? Math.round((earned / max) * 5) : 0;
  return (
    <span className="tu-stars" aria-label={`${earned} out of ${max}`}>
      {[0, 1, 2, 3, 4].map((i) => (
        <span key={i} className={i < filled ? '' : 'off'}>★</span>
      ))}
    </span>
  );
};

export const FeedbackCard = ({ instance }) => {
  const { props } = instance;
  const v = VERDICT[props.verdict] || VERDICT.partial;
  const strengths = props.strengths || [];
  const improvements = props.improvements || [];
  return (
    <div className={`tu-card tu-feedback ${props.verdict || 'partial'}`}>
      <div className="tu-fb-head">
        <span className="tu-fb-verdict">{v.icon} {v.label}</span>
        {props.error_type && <span className="tu-fb-type">{String(props.error_type).replace(/_/g, ' ')}</span>}
      </div>
      {props.score && (
        <div className="tu-fb-score">
          Score: <b>{props.score.earned} / {props.score.max}</b>
          <Stars earned={props.score.earned} max={props.score.max} />
        </div>
      )}
      {(strengths.length || improvements.length) ? (
        <div className="tu-fb-grid">
          <div>
            <div className="tu-fb-col-h">Summary</div>
            <div className="tu-fb-diag">{renderMarkdown(props.diagnosis_md || '')}</div>
            {improvements.length > 0 && (
              <>
                <div className="tu-fb-col-h" style={{ marginTop: 12 }}>To improve</div>
                <ul className="tu-fb-list im">
                  {improvements.map((s, i) => <li key={i}><span className="mk">◯</span><span>{s}</span></li>)}
                </ul>
              </>
            )}
          </div>
          <div>
            {strengths.length > 0 && (
              <>
                <div className="tu-fb-col-h">What you did well</div>
                <ul className="tu-fb-list ok">
                  {strengths.map((s, i) => <li key={i}><span className="mk">✓</span><span>{s}</span></li>)}
                </ul>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="tu-fb-diag" style={{ marginTop: 10 }}>{renderMarkdown(props.diagnosis_md || '')}</div>
      )}
      {props.fix_md && <div className="tu-fb-fix">→ {renderMarkdown(props.fix_md)}</div>}
    </div>
  );
};

const CHIP_ICON = {
  reattempt: '↻', break_down: '🪜', similar: '≈', explain: '💬', skip: '⏭', reveal: '👁', custom: '·',
};

export const ActionChips = ({ instance, emit, busy }) => {
  const { props } = instance;
  const [used, setUsed] = useState(false);
  return (
    <div className="tu-chips" role="group" aria-label="What next?">
      {(props.chips || []).map((c) => (
        <button key={c.id} type="button" className="tu-chip action" disabled={busy || used}
          onClick={() => { setUsed(true); emit('chip_clicked', { id: c.id, action: c.action, payload: c.payload || null }); }}>
          <span aria-hidden="true">{CHIP_ICON[c.action] || '·'}</span> {c.label}
        </button>
      ))}
    </div>
  );
};

export const MisconceptionCallout = ({ instance, emit, busy }) => {
  const { props } = instance;
  const [gone, setGone] = useState(false);
  if (gone) return null;
  return (
    <div className={`tu-card tu-miscon ${props.severity || 'info'}`}>
      <div className="tu-miscon-head">
        ⚠️ You may be mixing up <b>{props.confused?.a}</b> and <b>{props.confused?.b}</b>
      </div>
      <div className="tu-miscon-body">{renderMarkdown(props.clarification_md || '')}</div>
      <div className="tu-cardactions">
        <button type="button" className="tu-ghost" disabled={busy}
          onClick={() => { setGone(true); emit('dismissed', {}); }}>
          Got it
        </button>
        <button type="button" className="tu-primary" disabled={busy}
          onClick={() => emit('learn_more_clicked', {})}>
          Explain the difference
        </button>
      </div>
    </div>
  );
};
